import { ALL_SIGNAL_IDS, SIGNAL_OPTIONS } from '../data/mockOnboardingCatalog';
import SignalCards from '../components/SignalCards';
import SignalsMatchInsight from '../components/SignalsMatchInsight';
import { useOnboardingSelectedSignalsCount } from '../hooks/useOnboardingSelectedSignalsCount';
import { useSkipOnboardingAnalysisAnimation } from '../hooks/useSkipOnboardingAnalysisAnimation';
import {
  STEP_COPY,
  ONBOARDING_INSIGHT_SECTION_CLASS,
  ONBOARDING_SCROLL_AREA_CLASS,
  ONBOARDING_STEP_BODY_CLASS,
} from '../constants';

interface SignalsStepProps {
  selected: string[];
  isActive: boolean;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}

export default function SignalsStep({
  selected,
  isActive,
  onToggle,
  onSelectAll,
  onClearAll,
}: SignalsStepProps) {
  const skipInitialAnimation = useSkipOnboardingAnalysisAnimation(selected);
  const {
    total: matchCount,
    isLoading: isCountLoading,
    error: hasCountError,
  } = useOnboardingSelectedSignalsCount(selected, isActive);

  const allSelected = selected.length === ALL_SIGNAL_IDS.length;
  const hasSelectedSignals = selected.length > 0;
  const contentClass = 'mx-auto w-full max-w-2xl';
  const horizontalInsetClass = 'px-1 sm:px-0';

  return (
    <div className={ONBOARDING_STEP_BODY_CLASS}>
      <div className={`mb-3 flex shrink-0 justify-center sm:mb-4 ${horizontalInsetClass}`}>
        <label
          className={`inline-flex w-auto max-w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent px-2.5 py-2 transition-colors duration-150 sm:max-w-sm sm:gap-2.5 sm:px-3 sm:py-2.5 ${
            allSelected
              ? 'bg-white text-gray-900 ring-2 ring-inset ring-gray-600'
              : 'bg-background text-gray-900 ring-1 ring-inset ring-gray-200 hover:ring-gray-300'
          }`}
        >
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => (allSelected ? onClearAll() : onSelectAll())}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-emerald-600 focus:ring-emerald-600 sm:h-4 sm:w-4"
          />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 sm:gap-x-2">
              <span className="text-xs font-medium text-gray-900 sm:text-sm">
                {STEP_COPY.signals.selectAllLabel}
              </span>
              <span className="rounded-full bg-emerald-600 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-white sm:px-2 sm:py-0.5 sm:text-[10px]">
                {STEP_COPY.signals.selectAllRecommended}
              </span>
            </span>
            <span className="mt-0.5 hidden text-xs leading-snug text-gray-500 sm:block">
              {STEP_COPY.signals.selectAllHint}
            </span>
          </span>
        </label>
      </div>

      <div
        className={`${ONBOARDING_SCROLL_AREA_CLASS} ${horizontalInsetClass}`}
      >
        <div className={`${contentClass} pb-2 sm:pb-3`}>
          <SignalCards options={SIGNAL_OPTIONS} selected={selected} onToggle={onToggle} />
        </div>
      </div>

      {hasSelectedSignals ? (
        <div className={`${ONBOARDING_INSIGHT_SECTION_CLASS} ${horizontalInsetClass}`}>
          <SignalsMatchInsight
            signalIds={selected}
            matchCount={matchCount}
            isCountLoading={isCountLoading}
            hasCountError={hasCountError}
            skipInitialAnimation={skipInitialAnimation}
          />
        </div>
      ) : null}
    </div>
  );
}
